import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FaiButton from "../../../MuiComponents/FaiButton";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import { ProductController } from "../../../controllers/productController";
import DetailForm from "../../../screens/Catalog/AddProductForm/component/ProductForm/component/DetailForm";
import VariantForm from "../../../screens/Catalog/AddProductForm/component/ProductForm/component/VariantForm";
import { Product } from "../../../types/productTypes";

interface EditDraftModalProps {
	open: boolean;
	onClose: () => void;
	product: { id: string } | null;
	onProductUpdated: () => void;
	onError?: (errorMessage: string) => void;
}

// Extended Product type to include masterCategoryId for edit form
interface ExtendedProduct extends Product {
	masterCategoryId?: string;
	brandName?: string;
}

function EditDraftModal({
	open,
	onClose,
	product,
	onProductUpdated,
	onError,
}: EditDraftModalProps) {
	const [loading, setLoading] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [productData, setProductData] = useState(null);
	const [formData, setFormData] = useState<ExtendedProduct>({
		skuCode: "",
		brand: "",
		productName: "",
		manufacturer: "",
		articleNumber: "",
		description: "",
		categoryId: "",
		productTypeId: "",
		metadata: {},
		variants: [],
		shippingEssentials: {
			length: 0,
			breadth: 0,
			height: 0,
			weight: 0,
		},
		// isReturnable: true,
		isDraft: true,
	});
	// Fetch product details on open
	useEffect(() => {
		const fetchProductDetails = async () => {
			if (!product?.id || !open) return;
			setLoading(true);
			setError(null);
			try {
				const response = await ProductController.getProductToUpdate(product.id);
				if (response?.data?.status === 200 && response.data.data) {
					const productData: any = response.data.data;
					console.log("Product data received:", productData);

					// If we have a categoryId (sub-category), fetch the master category
					let masterCategoryId = productData.categoryId;
					if (productData.categoryId) {
						try {
							console.log(
								"Fetching master category for sub-category ID:",
								productData.categoryId
							);
							const masterCategoryResponse =
								await ProductController.getMasterCategorybyCategory(
									productData.categoryId
								);
							console.log("Master category response:", masterCategoryResponse);
							if (
								masterCategoryResponse?.data?.status === 200 &&
								masterCategoryResponse.data.data
							) {
								// The API returns master category data, extract the master category ID
								const masterCategoryData = masterCategoryResponse.data.data;
								console.log("Master category data:", masterCategoryData.id);
								// The API should return the master category information
								// Try different possible field names based on the API response structure
								masterCategoryId = masterCategoryData.id;
								console.log("Final master category ID:", masterCategoryId);
							}
						} catch (masterCategoryError) {
							console.error(
								"Error fetching master category:",
								masterCategoryError
							);
							// Continue with the original categoryId if master category fetch fails
						}
					}
					setProductData(productData);

					setFormData({
						...productData,
						isDraft: productData.draft,
						brandName: productData.brand,
						// Set the master category ID for the form
						masterCategoryId: masterCategoryId,
					});

					console.log("Final form data set:", {
						...productData,
						brandName: productData.brand,
						masterCategoryId: masterCategoryId,
					});
				} else {
					setError("Failed to fetch product details");
				}
			} catch (err: any) {
				setError("Error fetching product details");
			} finally {
				setLoading(false);
			}
		};
		fetchProductDetails();
	}, [product, open]);

	// Save/Update logic
	const handleSave = async () => {
		if (!product?.id) return;
		setSaving(true);
		setError(null);
		setSuccess(null);
		try {
			if (formData.sizeChartMedia) {
				await ProductController.updateSizeChartMedia(
					product.id,
					formData.sizeChartMedia
				);
				delete formData.sizeChartMedia;
			}
			if (!!formData.variantIdsToBeRemoved?.length) {
				for (const variantId of formData.variantIdsToBeRemoved) {
					if (variantId) {
						await ProductController.deleteVariant(formData.id, variantId);
						// Remove the media property after upload
						// delete variant.media;
					}
				}
			}
			if (formData.variants) {
				for (const variant of formData.variants) {
					if(variant?.id?.includes("new")) delete variant.id;
					else if (variant.media && Array.isArray(variant.media)) {
						for (const mediaItem of variant.media) {
							if (mediaItem instanceof File && variant.id) {
								await ProductController.addVariantMedia(variant.id, mediaItem);
								// Remove the media property after upload
								delete variant.media;
							}
						}
					}
				}
			}

			// Create updatedformdata with only changed fields
			// const updatedformdata = getChangedFields(productData, formData);

			const response = await ProductController.updateProduct(
				product.id,
				formData
			);
			setSuccess("Product updated successfully");
			// toast.success("Product updated successfully");
			onProductUpdated();
			setTimeout(() => onClose(), 500);
		} catch (err: any) {
			console.log("err", err);
			// setError("Error updating product");
			// if (onError) onError("Error updating product");
		} finally {
			setSaving(false);
		}
	};

	// Helper function to get only changed fields
	const getChangedFields = (originalData: any, newData: any) => {
		const changedFields: any = {};

		// Helper function to deep compare objects
		const isEqual = (obj1: any, obj2: any): boolean => {
			if (obj1 === obj2) return true;
			if (obj1 == null || obj2 == null) return obj1 === obj2;
			if (typeof obj1 !== typeof obj2) return false;

			if (typeof obj1 !== "object") return obj1 === obj2;

			if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

			if (Array.isArray(obj1)) {
				if (obj1.length !== obj2.length) return false;
				for (let i = 0; i < obj1.length; i++) {
					if (!isEqual(obj1[i], obj2[i])) return false;
				}
				return true;
			}

			const keys1 = Object.keys(obj1);
			const keys2 = Object.keys(obj2);

			if (keys1.length !== keys2.length) return false;

			for (const key of keys1) {
				if (!keys2.includes(key)) return false;
				if (!isEqual(obj1[key], obj2[key])) return false;
			}

			return true;
		};

		// Compare each field in newData with originalData
		for (const key in newData) {
			if (newData.hasOwnProperty(key)) {
				// Skip certain fields that shouldn't be compared or sent
				if (key === "sizeChartMedia" || key === "masterCategoryId") {
					continue;
				}

				// Check if the field has changed
				if (!isEqual(originalData[key], newData[key])) {
					changedFields[key] = newData[key];
				}
			}
		}

		return changedFields;
	};

	return (
		<Dialog
			open={open}
			onClose={!saving ? onClose : undefined}
			fullWidth
			maxWidth="lg"
		>
			<DialogTitle>
				<FaiBox
					display="flex"
					justifyContent="space-between"
					alignItems="center"
				>
					<FaiTypography variant="h5">Edit Product</FaiTypography>
					<IconButton onClick={onClose} disabled={saving}>
						<CloseIcon />
					</IconButton>
				</FaiBox>
			</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<FaiBox
						display="flex"
						justifyContent="center"
						alignItems="center"
						py={4}
					>
						<CircularProgress />
					</FaiBox>
				) : error ? (
					<FaiBox
						display="flex"
						flexDirection="column"
						justifyContent="center"
						alignItems="center"
						py={4}
					>
						<FaiTypography color="error.main">{error}</FaiTypography>
					</FaiBox>
				) : (
					<>
						<DetailForm formData={formData} setFormData={setFormData} />
						<VariantForm formData={formData} setFormData={setFormData} />
					</>
				)}
			</DialogContent>
			<DialogActions>
				<FaiButton
					onClick={onClose}
					color="secondary"
					variant="outlined"
					disabled={saving}
				>
					Cancel
				</FaiButton>
				<FaiButton
					onClick={handleSave}
					color="primary"
					variant="contained"
					disabled={saving || loading || !!error}
				>
					{saving ? "Saving..." : "Save Changes"}
				</FaiButton>
			</DialogActions>
		</Dialog>
	);
}

export default EditDraftModal;
